import { dataAnalyticsExercises } from "~/features/computer/data/da/da.exercises";
import { digitalLiteracyExercises } from "~/features/computer/data/digital-literacy/digital-literacy.exercises";
import { dsExercises } from "~/features/computer/data/ds/ds.exercises";
import { emergingTechExercises } from "~/features/computer/data/emerging/emerging.exercises";
import { entrepreneurshipExercises } from "~/features/computer/data/entrepreneurship/entrepreneurship.exercises";
import { algorithmExercises } from "~/features/computer/data/exercises/algorithm.exercises";
import { expressionsExercises } from "~/features/computer/data/exercises/expressions.exercises";
import { loopsExercises } from "~/features/computer/data/exercises/loops.exercises";
import { operatorsExercises } from "~/features/computer/data/exercises/operators.exercises";
import { variablesExercises } from "~/features/computer/data/exercises/variables.exercises";
import { legalEthicsExercises } from "~/features/computer/data/legal-ethics/legal-ethics.exercises";
import { sdlcExercises } from "~/features/computer/data/sdlc/sdlc.exercises";
import type { ExerciseAnswerLetter, ExerciseMcq, TopicExercises, VerifiedExerciseMcq, VerifiedMcqUnit } from "~/features/computer/data/exercises/exercise.types";
import { answerKeys } from "~/features/computer/data/exercises/mcq.answer.keys";
import { defineVerifiedMcqUnit, letterToIndex, toTopicExercises, validateUnit } from "~/features/computer/data/exercises/mcq.integrity";

function asVerifiedQuestion(question: ExerciseMcq, unitNumber: number, questionNumber: number, correct: ExerciseAnswerLetter): VerifiedExerciseMcq {
  return {
    ...question,
    id: `u${unitNumber}-q${questionNumber}`,
    correct,
    correctIndex: letterToIndex(correct),
  };
}

function buildUnit(unitNumber: number, source: ExerciseMcq[], key: ExerciseAnswerLetter[], manualCore: ExerciseMcq[] = []): VerifiedMcqUnit {
  const combined = [...source, ...manualCore];
  const core = key.map((correct, index) => asVerifiedQuestion(combined[index], unitNumber, index + 1, correct));
  const extra = combined.slice(key.length).map((question, index) => ({
    ...question,
    id: `u${unitNumber}-extra-${index + 1}`,
  }));

  return defineVerifiedMcqUnit(
    { unit: `unit-${unitNumber}`, totalMcqs: key.length },
    core,
    extra,
  );
}

const unit2Source = [
  ...variablesExercises.mcqs,
  ...operatorsExercises.mcqs,
  ...expressionsExercises.mcqs,
  ...loopsExercises.mcqs,
];

const unit3Manual: ExerciseMcq[] = [
  {
    id: "alg9",
    question: "Problem solving me decomposition ka matlab kya hota hai?",
    options: ["Problem ko chhote parts me todna", "Code ko delete karna", "Output skip karna", "Random steps lena"],
    correctIndex: 1,
    explanation: "Decomposition me complex problem ko chhote manageable parts me divide kiya jata hai.",
  },
  {
    id: "alg10",
    question: "Flowchart ka basic faida kya hai?",
    options: ["Code ko compile karna", "Steps ko visual form me dikhana", "Memory badhana", "Loop hata dena"],
    correctIndex: 2,
    explanation: "Flowchart steps ko visual form me show karta hai taake logic samajhna easy ho.",
  },
];

const unit7Manual: ExerciseMcq[] = [
  {
    id: "le9",
    topic: "security-threats",
    anchor: "spyware",
    question: "Spyware ka simple matlab kya hota hai?",
    options: ["User ki activity secretly track karna", "Helpful backup system", "Fast internet tool", "Open source library"],
    correctIndex: 0,
    explanation: "Spyware chup kar data ya activity monitor karta hai.",
  },
];

const unit8Manual: ExerciseMcq[] = [
  {
    id: "dl9",
    topic: "online-libraries",
    anchor: "academic-resources",
    question: "Academic ya peer-reviewed material ka best source kya hota hai?",
    options: ["Random video comments", "Digital library ya journal database", "Unverified meme page", "Chat forward"],
    correctIndex: 1,
    explanation: "Academic material structured libraries aur journal platforms me milta hai.",
  },
  {
    id: "dl10",
    topic: "search",
    anchor: "keywords",
    question: "Broad search se bachne ke liye kya karna chahiye?",
    options: ["Single vague word likho", "Specific keywords use karo", "Date ignore karo", "Har result open karo"],
    correctIndex: 1,
    explanation: "Specific keywords zyada focused results laati hain.",
  },
];

const unit9Manual: ExerciseMcq[] = [
  {
    id: "en8",
    topic: "design-thinking",
    anchor: "steps",
    question: "Empathize step me kya kiya jata hai?",
    options: ["Budget final", "User ko samjha jata hai", "Tax file", "Ad run"],
    correctIndex: 1,
    explanation: "Empathize step me user need ko samjha jata hai.",
  },
  {
    id: "en9",
    topic: "business-plan",
    anchor: "core-parts",
    question: "Business plan ke baghair kya risk hota hai?",
    options: ["Clear roadmap nahi hota", "Profit guaranteed hota hai", "Customer automatically mil jata hai", "Cost zero ho jati hai"],
    correctIndex: 0,
    explanation: "Business plan na ho to direction aur strategy unclear reh sakti hai.",
  },
  {
    id: "en10",
    topic: "finance",
    anchor: "budgeting",
    question: "Budgeting ka basic faida kya hai?",
    options: ["Kharcha control me rehta hai", "Idea ko chupana", "Research avoid karna", "Brand naam badalna"],
    correctIndex: 0,
    explanation: "Budgeting se costs aur resources better manage hote hain.",
  },
];

export const verifiedMcqUnits = {
  unit1: buildUnit(1, sdlcExercises.mcqs, answerKeys.unit1),
  unit2: buildUnit(2, unit2Source, answerKeys.unit2),
  unit3: buildUnit(3, algorithmExercises.mcqs, answerKeys.unit3, unit3Manual),
  unit4: buildUnit(4, dsExercises.mcqs, answerKeys.unit4),
  unit5: buildUnit(5, dataAnalyticsExercises.mcqs, answerKeys.unit5),
  unit6: buildUnit(6, emergingTechExercises.mcqs, answerKeys.unit6),
  unit7: buildUnit(7, legalEthicsExercises.mcqs, answerKeys.unit7, unit7Manual),
  unit8: buildUnit(8, digitalLiteracyExercises.mcqs, answerKeys.unit8, unit8Manual),
  unit9: buildUnit(9, entrepreneurshipExercises.mcqs, answerKeys.unit9, unit9Manual),
} as const;

export const verifiedUnitReports = Object.entries(verifiedMcqUnits).map(([unitName, unit]) => ({
  unitName,
  unit,
  report: validateUnit(unit, answerKeys[unitName]),
}));

export const verifiedTopicExercises: Record<string, TopicExercises> = {
  sdlc: toTopicExercises(verifiedMcqUnits.unit1, sdlcExercises.short, sdlcExercises.long),
  algorithm: toTopicExercises(verifiedMcqUnits.unit3, algorithmExercises.short, algorithmExercises.long),
  "data-structures": toTopicExercises(verifiedMcqUnits.unit4, dsExercises.short, dsExercises.long),
  "data-analytics": toTopicExercises(verifiedMcqUnits.unit5, dataAnalyticsExercises.short, dataAnalyticsExercises.long),
  "emerging-tech": toTopicExercises(verifiedMcqUnits.unit6, emergingTechExercises.short, emergingTechExercises.long),
  "legal-ethics": toTopicExercises(verifiedMcqUnits.unit7, legalEthicsExercises.short, legalEthicsExercises.long),
  "digital-literacy": toTopicExercises(verifiedMcqUnits.unit8, digitalLiteracyExercises.short, digitalLiteracyExercises.long),
  entrepreneurship: toTopicExercises(verifiedMcqUnits.unit9, entrepreneurshipExercises.short, entrepreneurshipExercises.long),
};
